import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Plus, ArrowDownCircle, ArrowUpCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { trustService } from '../../services/api';
import RecordTrustPaymentModal from './RecordTrustPaymentModal';
import WithdrawFromTrustModal from './WithdrawFromTrustModal';

const TRANSACTION_TYPE_COLORS = {
  deposit: 'text-green-600',
  withdrawal: 'text-red-600',
  transfer: 'text-blue-600',
  interest: 'text-purple-600',
  refund: 'text-orange-600',
  adjustment: 'text-gray-600',
  fee_earned: 'text-yellow-600',
};

const CaseTrustSection = ({ caseId, clientId, clientName }) => {
  const [trustAccount, setTrustAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const fetchTrustData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const accountsRes = await trustService.getTrustAccounts({ client_id: clientId });
      const accounts = accountsRes.data || [];
      if (accounts.length > 0) {
        const account = accounts[0];
        setTrustAccount(account);
        // Fetch recent transactions for this account
        const txRes = await trustService.getAccountTransactions(account.id, { limit: 5 });
        setTransactions(txRes.data || []);
      } else {
        setTrustAccount(null);
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching trust data:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchTrustData();
  }, [fetchTrustData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <DollarSign className="mr-2 h-5 w-5" />
          Trust Account
        </h3>
        <p className="text-gray-400 text-sm">Loading trust account...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center">
          <DollarSign className="mr-2 h-5 w-5" />
          Trust Account
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTrustData}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {trustAccount && (
            <Link
              to={`/trust-accounts/${trustAccount.id}`}
              className="text-yellow-600 hover:text-yellow-700 text-sm flex items-center gap-1"
            >
              View Details <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>

      {!trustAccount ? (
        <div className="text-center py-6">
          <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm mb-3">No trust account for this client</p>
          <button
            onClick={() => setShowDepositModal(true)}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-yellow-600 inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Create & Deposit
          </button>
        </div>
      ) : (
        <>
          {/* Balance Cards */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Balance</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(trustAccount.current_balance)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Available</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(trustAccount.available_balance)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Reserved</p>
              <p className="text-lg font-semibold text-orange-500">
                {formatCurrency(trustAccount.reserved_balance)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowDepositModal(true)}
              className="flex-1 bg-green-50 text-green-700 border border-green-200 px-3 py-2 rounded-lg text-sm hover:bg-green-100 inline-flex items-center justify-center gap-1"
            >
              <ArrowDownCircle className="h-4 w-4" />
              Deposit
            </button>
            <button
              onClick={() => setShowWithdrawModal(true)}
              className="flex-1 bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-lg text-sm hover:bg-red-100 inline-flex items-center justify-center gap-1"
            >
              <ArrowUpCircle className="h-4 w-4" />
              Withdraw
            </button>
          </div>

          {/* Recent Transactions */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Recent Transactions</h4>
            {transactions.length === 0 ? (
              <p className="text-gray-400 text-sm">No transactions yet</p>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {tx.description || tx.transaction_type}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(tx.transaction_date)} &middot; {tx.transaction_type}
                      </p>
                    </div>
                    <span className={`text-sm font-semibold ml-3 ${TRANSACTION_TYPE_COLORS[tx.transaction_type] || 'text-gray-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <RecordTrustPaymentModal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        clientId={clientId}
        clientName={clientName}
        onPaymentRecorded={fetchTrustData}
      />
      <WithdrawFromTrustModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        clientId={clientId}
        clientName={clientName}
        trustBalance={trustAccount?.available_balance}
        onWithdrawalRecorded={fetchTrustData}
      />
    </div>
  );
};

export default CaseTrustSection;
